import {useReportStore} from '../features/products/useReportStore';
import useYDocStore from '../stores/useYDocStore';
import {downloadLatestVersionFromApi} from './api/reportVersions';
import {readSerializableHeaderFromYDoc} from './documentHeaderMeta';
import {hasActivePreviewDraft} from './previewDraft';

/**
 * Prefer in-memory editor/preview content over persisted snapshots (~10s collab debounce).
 *
 * @param {string} reportId
 * @param {import('prosemirror-view').EditorView|null|undefined} editorView
 * @returns {{content: object, references: Array, metadata: object|null}|null}
 */
export function resolveLiveExportSnapshot(reportId, editorView) {
    if (!reportId) {
        return null;
    }

    const liveContent = editorView?.state?.doc?.toJSON?.();
    if (liveContent) {
        const ydoc = useYDocStore.getState().ydoc;
        let references = [];
        if (ydoc) {
            const {extractReferencesFromYDoc} = require('./yjsSnapshot');
            references = extractReferencesFromYDoc(ydoc);
        }
        return {
            content: liveContent,
            references,
            metadata: ydoc ? readSerializableHeaderFromYDoc(ydoc) : null,
        };
    }

    const previewDraft = useReportStore.getState().previewDraft;
    if (hasActivePreviewDraft(previewDraft, reportId)) {
        return {
            content: previewDraft.content,
            references: previewDraft.references || [],
            metadata: previewDraft.metadata || null,
        };
    }

    return null;
}

/**
 * Resolve export payload: live editor → preview draft → latest persisted API snapshot.
 *
 * @param {string} reportId
 * @param {import('prosemirror-view').EditorView|null|undefined} editorView
 * @returns {Promise<{content: object, references: Array, metadata: object|null}|null>}
 */
export async function resolveExportSnapshot(reportId, editorView) {
    const live = resolveLiveExportSnapshot(reportId, editorView);
    if (live?.content) {
        return live;
    }

    try {
        return await downloadLatestVersionFromApi(reportId);
    } catch (_) {
        return null;
    }
}
